import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { EmptyListReason } from 'components/common/noTableContentInfo/NoTableContentInfo';
import { useEmptyListReason } from 'hooks/table/useEmptyListReason';
import { userModelToUserOnViewWithAccessLevel } from 'shared/domain/user/mapping/toView';
import type { LabelledUserModel, UserModel } from 'shared/domain/user/types/model';
import type { GetUserOnViewWithAccessLevelOnIconViewById } from 'shared/domain/user/types/view';
import { findById } from 'shared/utils/id/findById';
import { useProjectOrganizationId } from 'store/project';
import { useEntityCollectionInitializer } from '../../entityCollection';
import type { SearchActions, StoreData } from '../../types';
import { findProjectUsersMatchingPhrase } from '../model';
import { useProjectUsersStore } from '../store';
import type { ProjectUsersActions } from '../types';

/** Loads the users of the selected project and keeps them current. */
export function useProjectUsersInitializer(): void {
  useEntityCollectionInitializer(useProjectUsersStore.use.actions());
}

/** Users of the selected project, unfiltered, with their loading state. */
export function useProjectUsers(): StoreData<UserModel> {
  return useProjectUsersStore.use.data();
}

/** Project users actions (stable references). */
export function useProjectUsersActions(): ProjectUsersActions {
  return useProjectUsersStore.use.actions();
}

export interface ProjectUsersSearch {
  /** The users matching the phrase, labelled as in the organization. */
  items: LabelledUserModel[];
  loading: boolean;
  emptyListReason: EmptyListReason | undefined;
  searchPhrase: string;
  setSearchPhrase: SearchActions['setSearchPhrase'];
}

/** The users table: the project users matching its search phrase. */
export function useProjectUsersSearch(): ProjectUsersSearch {
  const { items, loading } = useProjectUsersStore.use.data();
  const { phrase } = useProjectUsersStore.use.search();
  const { setSearchPhrase } = useProjectUsersStore.use.actions();
  const organizationId = useProjectOrganizationId();

  const matchingUsers = useMemo(
    () => findProjectUsersMatchingPhrase(items, phrase, organizationId),
    [items, phrase, organizationId]
  );
  const emptyListReason = useEmptyListReason(phrase, matchingUsers.length, 0, loading);

  return { items: matchingUsers, loading, emptyListReason, searchPhrase: phrase, setSearchPhrase };
}

/** Maps a project user by id to the issue views' user with their access level. */
export function useGetUserOnViewWithAccessLevel(): GetUserOnViewWithAccessLevelOnIconViewById {
  const { items } = useProjectUsersStore.use.data();
  const intl = useIntl();
  const organizationId = useProjectOrganizationId();

  return useCallback(
    (userId, userAccesses, sites, processes) => {
      const userModel = findById(items, userId, 'project users getUserOnViewWithAccessLevel');
      if (!userModel) {
        return null;
      }
      return userModelToUserOnViewWithAccessLevel({ userModel, userAccesses, sites, processes, intl, organizationId });
    },
    [items, intl, organizationId]
  );
}
